import {Component, HostListener, OnInit} from '@angular/core';
import { TriggerRule } from '../../model/trigger-rule';
import { SortOrder } from '../../model/sort-order';
import { TriggerRuleFilters } from '../../model/filters/trigger-rule-filters';
import { TriggerRulesService } from '../../services/trigger-rules.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {CommonMessagesService} from '../../services/common-messages.service';
import {TenantsService} from '../../services/tenants.service';
import { AuthGuard } from '../../common/auth-guard.service';
import { Tenant } from '../../model/tenant';
import {FiltersService} from "../../services/filters.service";

@Component({
  selector: 'app-trigger-rules',
  templateUrl: './trigger-rules.component.html',
  styleUrls: ['./trigger-rules.component.scss']
})
export class TriggerRulesComponent implements OnInit {

  public rules: TriggerRule[];
  public totalRules: number;
  public loadingRules: boolean;
  public tenants: Tenant[];

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: TriggerRuleFilters;
  public filtersFilled: boolean;

  constructor(private triggerRulesService: TriggerRulesService,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              public authGuardService: AuthGuard,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<TriggerRuleFilters>) {
    this.currentFilters = new TriggerRuleFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    if (this.authGuardService.superAdminRequired()) {
      this.tenantsService.getTenants().subscribe(response => {
        this.tenants = response.tenants
          .sort((a, b) => a.name > b.name ? 1 : -1);
        Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      });
    } else {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    }
    this.rules = new Array(this.rows);
  }

  private loadRules(offset: number, limit: number): void {
    this.loadingRules = true;
    this.rules = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.triggerRulesService.getTriggerRules(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters: this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.rules = response.triggerRules;
        let dataKey = 0;
        this.rules.forEach(rule => {
          if (rule.appliedTo) {
            rule.appliedTo = JSON.parse(rule.appliedTo);
          }
          rule['dataKey'] = dataKey++;
        });
        this.totalRules = response.totalElements;
        this.loadingRules = false;
      });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadRules(event.first / event.rows, event.rows);
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  resetFilters(): void {
    this.currentFilters = new TriggerRuleFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true){
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadRules(this.first / this.rows, this.rows);
  }

  confirmAndDelete(triggerRule: TriggerRule): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      const organizationUuid = triggerRule.organization ? triggerRule.organization.uuid : null;
      this.deleteTriggerRule(triggerRule.tenant.uuid, organizationUuid, triggerRule.name);
    });
  }

  deleteTriggerRule(tenantUuid: string, organizationUuid: string, name: string): void {
    this.tenantsService.deleteTenantTriggerRule(tenantUuid, organizationUuid, name).toPromise().then(() => {
      this.showDeleteInProgressMessage();
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) { return; }
    const identifiers = this.selected.map(item => ({
      tenantUuid: item.tenant.uuid,
      organizationUuid: item.organization ? item.organization.uuid : null,
      name: item.name}));

    this.triggerRulesService.deleteTriggerRules(identifiers).toPromise().then(() => {
      this.showDeleteInProgressMessage();
      this.loadRules(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  public showDeleteInProgressMessage(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.generic.deleteInProgress');
    this.messageService.add({severity: 'warn', summary, detail, sticky: true});
  }

  trimFieldsForm(filters: TriggerRuleFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
